import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { MaskedLines, EASE } from "@/components/motion/Reveal";

const HERO_IMAGE =
  "https://images.pexels.com/photos/2606383/pexels-photo-2606383.jpeg?auto=compress&cs=tinysrgb&w=1920";

export function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} id="startseite" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-navy-deep">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Modern architecture"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-navy-deep/55" />
      <div className="absolute inset-0 blueprint-grid-dark opacity-60" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy-deep/80 to-transparent" />

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-5 pb-16 pt-32 md:px-8 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          data-testid="hero-overline"
          className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-white/70 md:text-sm"
        >
          {t.hero.overline}
        </motion.p>

        <MaskedLines
          lines={t.hero.lines}
          className="max-w-5xl font-display text-4xl font-bold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl"
          baseDelay={0.25}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          data-testid="hero-subheadline"
          className="mt-7 max-w-2xl text-base leading-relaxed text-white/80 md:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#kontakt"
            data-testid="hero-cta-primary"
            className="group flex items-center gap-2 bg-white px-7 py-4 text-sm font-semibold text-navy transition-colors hover:bg-brand hover:text-white"
          >
            {t.hero.primary}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#referenzen"
            data-testid="hero-cta-secondary"
            className="group flex items-center gap-2 border border-white/40 px-7 py-4 text-sm font-semibold text-white transition-colors hover:border-white hover:bg-white/10"
          >
            {t.hero.secondary}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-14 grid gap-px border border-white/15 bg-white/15 sm:grid-cols-3"
          data-testid="hero-meta"
        >
          {t.hero.meta.map((m, i) => (
            <div key={i} className="flex items-center gap-3 bg-navy-deep/60 px-5 py-4 backdrop-blur-sm">
              <span className="font-mono text-xs text-brand">0{i + 1}</span>
              <span className="text-xs font-medium text-white/80 md:text-sm">{m}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
